import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import LotterySearchModal from '../../components/LotterySearchModal';
import DateField from '../../components/DateField';
import { resolveLotteryByCode } from '../../utils/lotteryLookup';
import {
  submitWinningNumber, peekWinningNumber, fetchWinningNumberSummary, fetchWinningNumberSource,
} from '../../api/winningNumbers';
import { todayISO } from '../../utils/date';
import { fmtWin4 } from '../../utils/lottery';
import './WinningNumbersPage.css';

const fmtMoney = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const WinningNumbersPage = () => {
  const [date, setDate] = useState(todayISO());
  const [lottery, setLottery] = useState('1');
  const [lotteryName, setLotteryName] = useState('NY MIDI');
  const [lotteryLookupError, setLotteryLookupError] = useState('');
  const [showLotteryModal, setShowLotteryModal] = useState(false);
  const [source, setSource] = useState(null); // { site_url, api_url, lottery_name } | null
  const [numbers, setNumbers] = useState('');
  const [win4, setWin4] = useState('');

  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [summary, setSummary] = useState({ rows: [], grand_total: 0, grand_tickets: 0 });

  const loadSummary = useCallback(async () => {
    try {
      const data = await fetchWinningNumberSummary({ date });
      setSummary(data);
    } catch (err) {
      setSummary({ rows: [], grand_total: 0, grand_tickets: 0 });
    }
  }, [date]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Lien vers la source lotomobil d'ou le scraper lit les lots gagnants de cette
  // loterie — affiche a cote de "Check Source" (null si tirage non gere).
  useEffect(() => {
    let cancelled = false;
    fetchWinningNumberSource({ tirage: lottery })
      .then((s) => { if (!cancelled) setSource(s); })
      .catch(() => { if (!cancelled) setSource(null); });
    return () => { cancelled = true; };
  }, [lottery]);

  const handleCheckSource = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setChecking(true);
    try {
      const src = await peekWinningNumber({ tirage: lottery, date });
      setNumbers(src.numbers);
      setWin4(src.win4);
      setSuccess(`Source : ${src.numbers} / ${fmtWin4(src.win4)} (tirage ${src.time}). Verifie puis Execute.`);
    } catch (err) {
      // apiRequest leve une Error dont le message est le JSON { detail, reason }.
      let msg = 'Resultat pas encore disponible sur la source.';
      try {
        const parsed = JSON.parse(err.message);
        if (parsed && parsed.detail) msg = parsed.detail;
      } catch (_) { /* garde le message par defaut */ }
      setError(msg);
    } finally {
      setChecking(false);
    }
  };

  const handleExecute = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!numbers || !win4) {
      setError('NUMBER et WIN4 sont requis.');
      return;
    }
    setSaving(true);
    try {
      const result = await submitWinningNumber({ tirage: lottery, date, numbers, win4 });
      setSuccess(`Enregistre — ${result.lines_recalculated} fiche(s) recalculee(s).`);
      loadSummary();
    } catch (err) {
      setError('Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  // Quitter le champ Loterie : résout le code tapé -> nom affiché à côté.
  const handleLotteryBlur = async () => {
    const c = String(lottery).trim();
    if (c === '') { setLotteryLookupError(''); return; }
    if (c === '0') { setLotteryLookupError('Loterie introuvable'); return; } // pas de "TOUS" ici
    const res = await resolveLotteryByCode(c);
    if (res) {
      setLottery(res.code);
      setLotteryName(res.name);
      setLotteryLookupError('');
    } else {
      setLotteryLookupError('Loterie introuvable');
    }
  };

  return (
    <Layout>
      <p className="wn-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/central" className="bc-link">Central</Link>
        {' >> '}
        <span className="bc-current">Winning Numbers</span>
      </p>

      <div className="wn-card">
        <div className="wn-card-header">
          <span className="wn-card-title">Winning Numbers</span>
        </div>
        <div className="wn-card-body">
          <form onSubmit={handleExecute} className="wn-form">
            <div className="wn-row">
              <label className="wn-label">Date</label>
              <DateField className="wn-input wn-input-date" value={date} onChange={setDate} />
            </div>

            <div className="wn-row">
              <label className="wn-label">Lottery</label>
              <input
                className="wn-input wn-input-sm"
                type="text"
                inputMode="numeric"
                value={lottery}
                onChange={e => { setLottery(e.target.value); setLotteryLookupError(''); }}
                onFocus={e => e.target.select()}
                onBlur={handleLotteryBlur}
              />
              <span className="wn-icon-btn" onClick={() => setShowLotteryModal(true)}>🔍</span>
              {lotteryLookupError
                ? <span className="wn-lottery-name wn-lookup-err">{lotteryLookupError}</span>
                : lotteryName && <span className="wn-lottery-name">{lotteryName}</span>}
            </div>

            {showLotteryModal && (
              <LotterySearchModal
                onSelect={(code, name) => {
                  setLottery(String(code));
                  setLotteryName(name || '');
                  setLotteryLookupError('');
                }}
                onClose={() => setShowLotteryModal(false)}
                allowAll={false}
              />
            )}

            <div className="wn-results-box">
              <div className="wn-results-row">
                <span className="wn-results-label">NUMBER MIDDAY</span>
                <span className="wn-results-sublabel">NUMBERS</span>
                <input
                  className="wn-input wn-input-sm"
                  type="text"
                  value={numbers}
                  onChange={e => setNumbers(e.target.value)}
                />
              </div>

              <div className="wn-results-row">
                <span className="wn-results-label">WIN4 MIDDAY</span>
                <span className="wn-results-sublabel">WIN 4</span>
                <input
                  className="wn-input wn-input-sm"
                  type="text"
                  value={win4}
                  onChange={e => setWin4(e.target.value)}
                />
              </div>

              <div className="wn-confirm-row">
                <span className="wn-confirm-label">Confirm</span>
                <span className="wn-icon-btn">♻️</span>
              </div>

              <div className="wn-source-row">
                <a href="#/" className="wn-check-source" onClick={handleCheckSource}>
                  {checking ? 'Lecture source...' : 'Check Source'}
                </a>
                {source && (
                  <a
                    className="wn-source-link"
                    href={source.api_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Source scrapée : ${source.lottery_name} (lotomobil)`}
                  >
                    🔗 source ({source.lottery_name})
                  </a>
                )}
              </div>
            </div>

            {error && <p className="wn-error">{error}</p>}
            {success && <p className="wn-success">{success}</p>}

            <button type="submit" className="wn-execute-btn" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Execute'}
            </button>
          </form>
        </div>
      </div>

      <div className="wn-card wn-summary-card">
        <div className="wn-card-header">
          <span className="wn-card-title">Lots gagnants — {date}</span>
        </div>
        <div className="wn-card-body">
          <table className="wn-summary-table">
            <thead>
              <tr>
                <th>Lot</th>
                <th>Tirage</th>
                <th>Numéro</th>
                <th>Win4</th>
                <th>Statut</th>
                <th>Fiches gagnées</th>
                <th>Somme totale</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.length === 0 && (
                <tr><td colSpan="7" className="wn-summary-empty">Aucun lot gagnant saisi pour cette date.</td></tr>
              )}
              {summary.rows.map((r) => (
                <tr key={r.tirage_code}>
                  <td>{r.tirage_code}</td>
                  <td>{r.tirage_name}</td>
                  <td className="wn-summary-num">{r.numbers}</td>
                  <td className="wn-summary-num">{fmtWin4(r.win4)}</td>
                  <td>
                    <span className={`wn-status wn-status-${r.source}${r.scope === 'company' ? ' wn-status-company' : ''}`}>
                      {r.status_label}
                    </span>
                  </td>
                  <td className="wn-summary-center">{r.winning_tickets}</td>
                  <td className="wn-summary-money">{fmtMoney(r.total_gain)}</td>
                </tr>
              ))}
            </tbody>
            {summary.rows.length > 0 && (
              <tfoot>
                <tr className="wn-summary-total">
                  <td colSpan="5">Total</td>
                  <td className="wn-summary-center">{summary.grand_tickets}</td>
                  <td className="wn-summary-money">{fmtMoney(summary.grand_total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default WinningNumbersPage;
